import { Link } from "react-router-dom";

const LAST_UPDATED = "29 April 2026";

export function Privacy() {
  return (
    <section className="mx-auto max-w-3xl px-6 py-12">
      <h1 className="font-serif text-4xl tracking-tight">Privacy Policy</h1>
      <p className="mt-3 text-sm text-neutral-500">Last updated: {LAST_UPDATED}</p>

      <div className="mt-8 space-y-8 text-neutral-700 leading-relaxed">
        <p>
          back2life.ai ("we", "us", "our") turns photographs into short
          AI-generated video stories. We take privacy seriously. This policy
          explains what we collect, how we use it, who we share it with, and
          the rights you have over your data.
        </p>

        <Section title="1. Who we are">
          <p>
            back2life.ai is operated from Australia. For privacy questions you
            can reach us at{" "}
            <a className="underline" href="mailto:contact@craft-codex.com">
              contact@craft-codex.com
            </a>{" "}
            or via our <Link to="/contact" className="underline">contact form</Link>.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Account information.</strong> When you sign up we collect
              your email address and a password (stored only as a hash) via
              Amazon Cognito.
            </li>
            <li>
              <strong>Photos you upload.</strong> The image you submit to
              generate a video is stored temporarily in Amazon S3 and processed
              by our AI providers.
            </li>
            <li>
              <strong>Generated videos.</strong> Output videos are stored in
              Amazon S3 against your account so you can re-watch and download
              them.
            </li>
            <li>
              <strong>Payment information.</strong> Card details are entered
              directly with our payment processor (Stripe) and never touch our
              servers. We receive a transaction reference, the amount, and the
              email you used at checkout.
            </li>
            <li>
              <strong>Usage data.</strong> We use Google Analytics to understand
              how the site is used (pages visited, approximate location,
              device/browser). Analytics cookies are set on your browser.
            </li>
            <li>
              <strong>Support requests.</strong> If you contact us we keep your
              name, email and message so we can reply.
            </li>
          </ul>
        </Section>

        <Section title="3. How we use your information">
          <ul className="list-disc pl-5 space-y-2">
            <li>To deliver the video generation service you requested.</li>
            <li>To process your payment and send you receipts.</li>
            <li>To send transactional emails (sign-up confirmation, password reset, payment receipts).</li>
            <li>To detect abuse and screen content for safety (see §5).</li>
            <li>To improve the product and fix bugs.</li>
            <li>To comply with legal obligations.</li>
          </ul>
          <p className="mt-3">
            We do <strong>not</strong> sell your personal data, and we do not
            use your photos or generated videos to train AI models.
          </p>
        </Section>

        <Section title="4. Who we share data with">
          <p>We rely on the following sub-processors to run the service:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>
              <strong>Amazon Web Services</strong> — hosting, storage,
              authentication, email delivery (region: ap-southeast-2 Sydney).
            </li>
            <li>
              <strong>FAL.ai</strong> — image-to-video generation (Pixverse,
              Veo 3.1, Grok Imagine).
            </li>
            <li>
              <strong>OpenAI</strong> — image and prompt analysis, content
              moderation, story generation.
            </li>
            <li>
              <strong>Stripe</strong> — payment processing.
            </li>
            <li>
              <strong>Google Analytics</strong> — anonymised site analytics.
            </li>
          </ul>
          <p className="mt-3">
            Each provider acts on our instructions and is bound by their own
            privacy and security obligations. We do not share your data for
            advertising purposes.
          </p>
        </Section>

        <Section title="5. Content moderation">
          <p>
            Uploaded images and prompts are automatically screened with an AI
            classifier to block nudity, sexual content involving minors,
            graphic violence and other prohibited material. Rejected uploads
            are not stored long-term. Repeated abuse may result in account
            termination.
          </p>
        </Section>

        <Section title="6. How long we keep data">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Uploaded source photos:</strong> automatically deleted
              from S3 after 7 days.
            </li>
            <li>
              <strong>Generated videos:</strong> retained while your account is
              active, or until you delete them from "My videos".
            </li>
            <li>
              <strong>Account data:</strong> retained while your account is
              active. When you delete your account we permanently remove your
              videos and personal data within 30 days.
            </li>
            <li>
              <strong>Payment records:</strong> retained by us and Stripe for
              up to 7 years for tax and accounting purposes.
            </li>
            <li>
              <strong>Support emails:</strong> retained up to 2 years.
            </li>
          </ul>
        </Section>

        <Section title="7. Your rights">
          <p>
            Depending on where you live, you have rights to access, correct,
            export, restrict or delete the personal data we hold about you, and
            to object to certain processing. These include rights under the
            Australian Privacy Act 1988, the EU/UK General Data Protection
            Regulation, and the California Consumer Privacy Act.
          </p>
          <p className="mt-3">
            You can delete your videos at any time from "My videos". To delete
            your entire account or exercise any other right, email{" "}
            <a className="underline" href="mailto:contact@craft-codex.com">
              contact@craft-codex.com
            </a>
            . We will respond within 30 days.
          </p>
        </Section>

        <Section title="8. Cookies">
          <p>
            We use a small number of strictly-necessary cookies to keep you
            signed in, plus Google Analytics cookies for usage statistics. You
            can block analytics cookies via your browser settings or a
            tracker-blocking extension without losing access to the service.
          </p>
        </Section>

        <Section title="9. International transfers">
          <p>
            Our servers are located in Sydney, Australia. Some sub-processors
            (FAL.ai, OpenAI, Stripe, Google) are based in the United States or
            Europe, which means your data may be transferred internationally.
            We rely on each provider's standard contractual clauses and
            equivalent safeguards.
          </p>
        </Section>

        <Section title="10. Children">
          <p>
            back2life.ai is not directed at children under 16. We do not
            knowingly collect personal data from children. If you believe a
            child has provided us with personal information, please contact us
            and we will delete it.
          </p>
        </Section>

        <Section title="11. Security">
          <p>
            We use HTTPS everywhere, encrypted storage at rest, scoped IAM
            roles, and signed pre-authorised URLs for image and video
            access. No system is perfectly secure — if you suspect your account
            has been compromised, please email us straight away.
          </p>
        </Section>

        <Section title="12. Changes to this policy">
          <p>
            We may update this policy from time to time. The "Last updated"
            date at the top will reflect the most recent change. For material
            changes we will notify you by email or via a notice on the site.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            Privacy questions, complaints or data requests:{" "}
            <a className="underline" href="mailto:contact@craft-codex.com">
              contact@craft-codex.com
            </a>
            .
          </p>
          <p className="mt-3">
            If you are in Australia and feel we have not handled your concern,
            you can also lodge a complaint with the Office of the Australian
            Information Commissioner (OAIC) at{" "}
            <a className="underline" href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">
              oaic.gov.au
            </a>
            .
          </p>
        </Section>
      </div>
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-serif text-2xl tracking-tight text-neutral-900">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}
